import React from 'react';
import type { I18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { Trans } from '@lingui/react/macro';
import styled from 'styled-components';

import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import { Display } from '@rover/kibble/core';
import MQ from '@rover/kibble/styles/MediaQuery';
import { Image } from '@rover/react-lib/src/components/images';
import { Alignment } from '@rover/react-lib/src/components/layouts/layout.constants';
import ResponsiveLayout from '@rover/react-lib/src/components/layouts/ResponsiveLayout';
import VerticalLayout from '@rover/react-lib/src/components/layouts/VerticalLayout';
import useI18nContext from '@rover/react-lib/src/context/useI18nContext';
import appShowcaseDaDkSource from '@rover/react-lib/src/images/homepage/localized-app-showcase-images/app-showcase-da-dk.webp';
import appShowcaseDeAtSource from '@rover/react-lib/src/images/homepage/localized-app-showcase-images/app-showcase-de-at.webp';
import appShowcaseDeChSource from '@rover/react-lib/src/images/homepage/localized-app-showcase-images/app-showcase-de-ch.webp';
import appShowcaseDeDeSource from '@rover/react-lib/src/images/homepage/localized-app-showcase-images/app-showcase-de-de.webp';
import appShowcaseEnCaSource from '@rover/react-lib/src/images/homepage/localized-app-showcase-images/app-showcase-en-ca.webp';
import appShowcaseEnGbSource from '@rover/react-lib/src/images/homepage/localized-app-showcase-images/app-showcase-en-gb.webp';
import appShowcaseEnIeSource from '@rover/react-lib/src/images/homepage/localized-app-showcase-images/app-showcase-en-ie.webp';
import appShowcaseEnUsSource from '@rover/react-lib/src/images/homepage/localized-app-showcase-images/app-showcase-en-us.webp';
import appShowcaseEsEsSource from '@rover/react-lib/src/images/homepage/localized-app-showcase-images/app-showcase-es-es.webp';
import appShowcaseFiFiSource from '@rover/react-lib/src/images/homepage/localized-app-showcase-images/app-showcase-fi-fi.webp';
import appShowcaseFrBeSource from '@rover/react-lib/src/images/homepage/localized-app-showcase-images/app-showcase-fr-be.webp';
import appShowcaseFrCaSource from '@rover/react-lib/src/images/homepage/localized-app-showcase-images/app-showcase-fr-ca.webp';
import appShowcaseFrChSource from '@rover/react-lib/src/images/homepage/localized-app-showcase-images/app-showcase-fr-ch.webp';
import appShowcaseFrFrSource from '@rover/react-lib/src/images/homepage/localized-app-showcase-images/app-showcase-fr-fr.webp';
import appShowcaseItChSource from '@rover/react-lib/src/images/homepage/localized-app-showcase-images/app-showcase-it-ch.webp';
import appShowcaseItItSource from '@rover/react-lib/src/images/homepage/localized-app-showcase-images/app-showcase-it-it.webp';
import appShowcaseNlBeSource from '@rover/react-lib/src/images/homepage/localized-app-showcase-images/app-showcase-nl-be.webp';
import appShowcaseNlNlSource from '@rover/react-lib/src/images/homepage/localized-app-showcase-images/app-showcase-nl-nl.webp';
import appShowcaseNoNoSource from '@rover/react-lib/src/images/homepage/localized-app-showcase-images/app-showcase-no-no.webp';
import appShowcasePlPlSource from '@rover/react-lib/src/images/homepage/localized-app-showcase-images/app-showcase-pl-pl.webp';
import appShowcasePtPtSource from '@rover/react-lib/src/images/homepage/localized-app-showcase-images/app-showcase-pt-pt.webp';
import appShowcaseSvSeSource from '@rover/react-lib/src/images/homepage/localized-app-showcase-images/app-showcase-sv-se.webp';
import appShowcaseTrainingEnUsSource from '@rover/react-lib/src/images/homepage/localized-app-showcase-images/training/app-showcase-dog-training-en-us.webp';
import getBrandSpecificValue from '@rover/shared/js/utils/getBrandSpecificValue';

import AndroidAppStoreButton from './AndroidAppStoreButton';
import IosAppStoreButton from './IosAppStoreButton';

const DEFAULT_APP_SHOWCASE_IMAGE = appShowcaseEnUsSource;
const DEFAULT_APP_TRAINING_SHOWCASE_IMAGE = appShowcaseTrainingEnUsSource;

export type ShowFeature = 'default' | 'training';

type Props = {
  featureToShow?: ShowFeature;
  altHeading?: React.ReactNode;
  altImageSrc?: string;
  /** Caps the showcase image width. Defaults to the width of the default image file. */
  imageMaxWidth?: string;
};

type FeatureConfig = {
  defaultImage: string;
  imageLocaleMap: Partial<Record<Locale, string>>;
  altText: string;
};

type FeatureConfigMap = Record<ShowFeature, FeatureConfig>;

const appShowcaseImageLocaleMap = {
  [Locale.DaDk]: appShowcaseDaDkSource,
  [Locale.DeAt]: appShowcaseDeAtSource,
  [Locale.DeCh]: appShowcaseDeChSource,
  [Locale.DeDe]: appShowcaseDeDeSource,
  [Locale.EnCa]: appShowcaseEnCaSource,
  [Locale.EnGb]: appShowcaseEnGbSource,
  [Locale.EnIe]: appShowcaseEnIeSource,
  [Locale.EnUs]: appShowcaseEnUsSource,
  [Locale.EsEs]: appShowcaseEsEsSource,
  [Locale.FiFi]: appShowcaseFiFiSource,
  'fr-be': appShowcaseFrBeSource,
  [Locale.FrCa]: appShowcaseFrCaSource,
  'fr-ch': appShowcaseFrChSource,
  [Locale.FrFr]: appShowcaseFrFrSource,
  'it-ch': appShowcaseItChSource,
  [Locale.ItIt]: appShowcaseItItSource,
  [Locale.NlBe]: appShowcaseNlBeSource,
  [Locale.NlNl]: appShowcaseNlNlSource,
  [Locale.NbNo]: appShowcaseNoNoSource,
  [Locale.PlPl]: appShowcasePlPlSource,
  'pt-pt': appShowcasePtPtSource,
  [Locale.SvSe]: appShowcaseSvSeSource,
};

const appShowcaseTrainingImageLocaleMap = {
  [Locale.EnUs]: appShowcaseTrainingEnUsSource,
};

const FEATURE_CONFIG = (i18n: I18n): FeatureConfigMap => ({
  default: {
    defaultImage: DEFAULT_APP_SHOWCASE_IMAGE,
    imageLocaleMap: appShowcaseImageLocaleMap,
    altText: i18n._(
      msg({
        message:
          'An iPhone and an Android device each displaying sitter search results from the Rover mobile app.',
        comment: 'Alt text for devices showing the app',
      })
    ),
  },
  training: {
    defaultImage: DEFAULT_APP_TRAINING_SHOWCASE_IMAGE,
    imageLocaleMap: appShowcaseTrainingImageLocaleMap,
    altText:
      'An iPhone and an Android device showing trainers on a search page in the Rover mobile app.',
  },
});

const DEFAULT_IMAGE_MAX_WIDTH = '394px';

const AppShowCaseImage = styled(Image)<{ $maxWidth: string }>`
  // defaults to the width of the image (file)
  max-width: ${({ $maxWidth }) => $maxWidth};
  // we use aspect-ratio instead of using height auto to reduce CLS
  aspect-ratio: 1.374;
`;

const ButtonContainer = styled(ResponsiveLayout)`
  margin-bottom: ${({ theme }) => theme.space['4x']};
  row-gap: ${({ theme }) => theme.space['2x']};

  ${MQ.NOT_XS.toString()} {
    column-gap: ${({ theme }) => theme.space['12x']};
  }
`;

const AppStoreCallout: React.FC<Props> = ({
  featureToShow = 'default',
  altHeading,
  altImageSrc,
  imageMaxWidth = DEFAULT_IMAGE_MAX_WIDTH,
}) => {
  const { locale } = useI18nContext();
  const { i18n } = useLingui();
  const featureConfig = FEATURE_CONFIG(i18n);
  const appShowcaseImageSrc =
    altImageSrc ||
    featureConfig[featureToShow].imageLocaleMap[locale] ||
    featureConfig[featureToShow].defaultImage;

  return (
    <VerticalLayout vAlign={Alignment.CENTER} hAlign={Alignment.CENTER}>
      <Display webProps={{ as: 'h3', responsive: false }} size="500" textAlign="center" mb="6x">
        {altHeading ??
          getBrandSpecificValue({
            rover: <Trans>Connect anywhere with the Rover app</Trans>,
            catInAFlat: <Trans>Connect anywhere with the Cat in a Flat app</Trans>,
          })}
      </Display>
      <ButtonContainer hAlign={Alignment.CENTER}>
        <IosAppStoreButton />
        <AndroidAppStoreButton />
      </ButtonContainer>
      {/* DEV-104195 TODO: translate training altText */}
      <AppShowCaseImage
        src={appShowcaseImageSrc}
        alt={featureConfig[featureToShow].altText}
        width="100%"
        $maxWidth={imageMaxWidth}
      />
    </VerticalLayout>
  );
};

export default AppStoreCallout;
